import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { StrategyUpdateEntry } from "../../../services/investorStrategyService";
import {
  ProductLookupEntry,
  ProductLookupKey,
  formatStrategyDate,
  formatStrategyUpdateMethod,
} from "../../../utils/strategyUtils";
import { formatWeight } from "../../../utils/numberUtils";
import { HISTORY_PAGE_SIZE, type StrategyIdProductMap } from "../../../hooks/useStrategyData";

type HistoryMethodFilter = "ALL" | "POST" | "PUT" | "DELETE";

function resolveHistoryProduct(
  entry: StrategyUpdateEntry,
  productLookup: Map<ProductLookupKey, ProductLookupEntry>,
  strategyIdProductMap: StrategyIdProductMap
): { product: ProductLookupEntry | null; entityType: string | undefined; productId: number | undefined } {
  let entityType = entry.payload.entityType as string | undefined;
  let productId = entry.payload.productId as number | undefined;

  if (!entityType || productId == null) {
    const fallback = strategyIdProductMap.get(entry.investorStrategyId);
    if (fallback) {
      entityType = fallback.entityType;
      productId = fallback.productId;
    }
  }

  const lookupKey =
    entityType && productId != null
      ? (`${entityType}-${productId}` as ProductLookupKey)
      : null;
  return { product: lookupKey ? productLookup.get(lookupKey) ?? null : null, entityType, productId };
}

function MethodBadge({
  method,
  appearance,
}: {
  method: "POST" | "PUT" | "DELETE";
  appearance: "default" | "alpha";
}) {
  const label = formatStrategyUpdateMethod(method);
  const classes = appearance === "alpha"
    ? {
        POST: "border-v7-success/30 bg-v7-success/10 text-v7-success",
        PUT: "border-v7-bronze/30 bg-v7-bronze/10 text-v7-bronze-dark",
        DELETE: "border-v7-rust/30 bg-v7-rust/10 text-v7-rust",
      }[method]
    : {
        POST: "bg-green-600/20 text-green-400 border-green-600/30",
        PUT: "bg-blue-600/20 text-blue-400 border-blue-600/30",
        DELETE: "bg-red-600/20 text-red-400 border-red-600/30",
      }[method];

  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${classes}`}
    >
      {label}
    </span>
  );
}

function getMethodDotColor(method: "POST" | "PUT" | "DELETE", appearance: "default" | "alpha"): string {
  switch (method) {
    case "POST":
      return appearance === "alpha" ? "bg-v7-success" : "bg-green-500";
    case "PUT":
      return appearance === "alpha" ? "bg-v7-bronze" : "bg-blue-500";
    case "DELETE":
      return appearance === "alpha" ? "bg-v7-rust" : "bg-red-500";
  }
}

export default function HistorySection({
  items,
  isLoading,
  error,
  productLookup,
  strategyIdProductMap,
  appearance = "default",
}: {
  items: StrategyUpdateEntry[];
  isLoading: boolean;
  error: string | null;
  productLookup: Map<ProductLookupKey, ProductLookupEntry>;
  strategyIdProductMap: StrategyIdProductMap;
  appearance?: "default" | "alpha";
}) {
  const isAlpha = appearance === "alpha";
  const [methodFilter, setMethodFilter] = useState<HistoryMethodFilter>("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  const prevFilterRef = useRef({ methodFilter, searchTerm });
  if (prevFilterRef.current.methodFilter !== methodFilter || prevFilterRef.current.searchTerm !== searchTerm) {
    prevFilterRef.current = { methodFilter, searchTerm };
    if (page !== 1) setPage(1);
  }

  if (isLoading) return null;

  if (error) {
    return (
      <div className="mt-2">
        <h2 className={`mb-4 text-lg font-semibold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>Strategy History</h2>
        <div className={`border p-4 ${isAlpha ? "rounded-v7-panel border-v7-rust/30 bg-v7-rust/5" : "rounded-lg border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20"}`}>
          <div className={`text-sm ${isAlpha ? "text-v7-rust" : "text-red-700 dark:text-red-400"}`}>{error}</div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mt-2">
        <h2 className={`mb-4 text-lg font-semibold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>Strategy History</h2>
        <p className={`text-sm ${isAlpha ? "text-v7-ink/50" : "text-gray-400 dark:text-gray-500"}`}>No history yet.</p>
      </div>
    );
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filtered = items.filter((entry) => {
    if (methodFilter !== "ALL" && entry.method !== methodFilter) return false;
    if (!normalizedSearch) return true;
    const { product, entityType, productId } = resolveHistoryProduct(entry, productLookup, strategyIdProductMap);
    const searchable = [
      product?.title,
      product?.providerName,
      entityType,
      productId != null ? String(productId) : null,
      ...Object.values(entry.payload).map(String),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return searchable.includes(normalizedSearch);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / HISTORY_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * HISTORY_PAGE_SIZE, safePage * HISTORY_PAGE_SIZE);

  const methods: HistoryMethodFilter[] = ["ALL", "POST", "PUT", "DELETE"];
  const methodLabels: Record<HistoryMethodFilter, string> = {
    ALL: "All",
    POST: "Created",
    PUT: "Updated",
    DELETE: "Removed",
  };

  return (
    <section className={isAlpha ? "mt-2 rounded-v7-panel border border-v7-ink/15 bg-v7-white p-5 md:p-6" : "mt-2"}>
      <h2 className={`mb-4 text-lg font-semibold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
        Strategy History
        <span className={`ml-2 text-xs font-normal ${isAlpha ? "text-v7-ink/50" : "text-gray-400 dark:text-gray-500"}`}>
          {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
        </span>
      </h2>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search className={`absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${isAlpha ? "text-v7-ink/50" : "text-gray-400"}`} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search history..."
            className={isAlpha
              ? "w-48 rounded-v7-control border border-v7-ink/20 bg-v7-canvas py-1.5 pl-8 pr-3 text-xs text-v7-ink placeholder-v7-ink/40 focus:outline-none focus:ring-1 focus:ring-v7-bronze-dark"
              : "w-48 rounded-lg border border-gray-200 bg-white py-1.5 pl-8 pr-3 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-amber-500/50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-200 dark:placeholder-gray-500"}
          />
        </div>
        <div className="flex items-center gap-1">
          {methods.map((m) => (
            <button
              key={m}
              onClick={() => setMethodFilter(m)}
              className={`px-2.5 py-1 text-[11px] font-medium rounded-md border transition-colors ${
                methodFilter === m
                  ? isAlpha
                    ? "border-v7-bronze-dark bg-v7-paper text-v7-ink"
                    : "border-amber-500 bg-white text-gray-900 dark:bg-white/10 dark:text-white"
                  : isAlpha
                    ? "border-v7-ink/15 text-v7-ink-soft hover:border-v7-ink/30 hover:text-v7-ink"
                    : "border-gray-200 text-gray-500 hover:text-gray-700 dark:border-rp-separator dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              {methodLabels[m]}
            </button>
          ))}
        </div>
      </div>

      <div className="relative">
        <div className={`absolute bottom-0 left-[5px] top-0 w-px border-l ${isAlpha ? "border-v7-ink/15" : "border-gray-200 dark:border-rp-separator"}`} />

        <div className="space-y-0">
          {pageItems.length === 0 ? (
            <p className={`py-3 pl-8 text-xs ${isAlpha ? "text-v7-ink/50" : "text-gray-400 dark:text-gray-500"}`}>
              No matching history entries.
            </p>
          ) : (
            pageItems.map((entry) => {
              const { product, entityType, productId } = resolveHistoryProduct(entry, productLookup, strategyIdProductMap);
              const displayPayload = Object.entries(entry.payload).filter(
                ([key]) => key !== "entityType" && key !== "productId" && key !== "position"
              );

              return (
                <div
                  key={entry.id}
                  className={`relative rounded-lg py-3 pl-8 transition-colors ${isAlpha ? "hover:bg-v7-paper" : "hover:bg-gray-100 dark:hover:bg-white/[0.02]"}`}
                >
                  <div
                    className={`absolute left-0 top-4 h-[11px] w-[11px] rounded-full border-2 ${isAlpha ? "border-v7-white" : "border-white dark:border-rp-surface"} ${getMethodDotColor(entry.method, appearance)}`}
                  />

                  <div className="flex items-center gap-2 mb-1">
                    <MethodBadge method={entry.method} appearance={appearance} />
                    <span className={`text-xs ${isAlpha ? "text-v7-ink/50" : "text-gray-400 dark:text-gray-500"}`}>
                      {formatStrategyDate(entry.datetimeUtc)}
                    </span>
                  </div>

                  {product ? (
                    <div className={`mt-1 text-sm ${isAlpha ? "text-v7-ink" : "text-gray-700 dark:text-gray-200"}`}>
                      {product.title}
                      <span className={`ml-2 text-xs ${isAlpha ? "text-v7-ink/50" : "text-gray-400 dark:text-gray-500"}`}>
                        by {product.providerName}
                      </span>
                    </div>
                  ) : entityType && productId != null ? (
                    <div className={`mt-1 text-sm ${isAlpha ? "text-v7-ink" : "text-gray-700 dark:text-gray-200"}`}>
                      {entityType} #{productId}
                    </div>
                  ) : null}

                  {displayPayload.length > 0 && (
                    <div className={`mt-1 font-mono text-xs ${isAlpha ? "text-v7-ink-soft" : "text-gray-500 dark:text-gray-400"}`}>
                      {displayPayload.map(([key, val]) => {
                        const display = key === "weight" && typeof val === "string"
                          ? formatWeight(parseFloat(val))
                          : key === "weight" && typeof val === "number"
                            ? formatWeight(val)
                            : String(val);
                        return (
                          <span key={key} className="mr-3">
                            <span className={isAlpha ? "text-v7-ink/50" : "text-gray-400 dark:text-gray-500"}>{key}:</span>{" "}
                            {display}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            className={`border p-2 transition-colors disabled:opacity-40 ${isAlpha
              ? "rounded-v7-control border-v7-ink/20 bg-v7-white text-v7-ink-soft hover:border-v7-ink"
              : "rounded-lg border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className={`px-3 text-sm ${isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-400"}`}>
            Page {safePage} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages}
            className={`border p-2 transition-colors disabled:opacity-40 ${isAlpha
              ? "rounded-v7-control border-v7-ink/20 bg-v7-white text-v7-ink-soft hover:border-v7-ink"
              : "rounded-lg border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </section>
  );
}
